import { create } from 'zustand';

/**
 * Arguments for displaying a rating modal
 */
interface RatingModalArgs {
  /** The order ID that the rating modal should display ratings for */
  orderId: number;
  /** Number of recent orders to include in rating data (defaults to 10) */
  recentOrderCount?: number;
}

/**
 * Central state for all modal types in the application
 * This allows for centralized modal management with priority ordering
 */
interface ModalState {
  /** Rating modal for order feedback - shown when user clicks "Rate" button */
  ratingModal: RatingModalArgs | null;
  // Add other modal types here as they're needed
  // exampleModal: ExampleModalArgs | null;
}

/**
 * Zustand store interface for modal management
 * Provides centralized modal state with priority-based rendering
 */
interface ModalStore extends ModalState {
  /** Set the rating modal arguments or null to close it */
  setRatingModal: (args: RatingModalArgs | null) => void;
  /** Clear all active modals */
  clearAllModals: () => void;
  /** Get the currently active modal based on priority order */
  getCurrentModal: () => { type: 'rating'; args: RatingModalArgs } | null;
  /** Reset the store to initial state (used for testing) */
  resetStore: () => void;
}

const initialState: ModalState = {
  ratingModal: null,
};

/**
 * Zustand store for managing application-wide modal state
 *
 * This store implements a priority-based modal system where only one modal
 * can be displayed at a time. The getCurrentModal() method determines which
 * modal should be shown based on a predefined priority order.
 *
 * Priority order (highest to lowest):
 * 1. Rating modal - for order feedback
 *
 * @example
 * ```tsx
 * const { setRatingModal, getCurrentModal } = useModalStore();
 *
 * // Show rating modal
 * setRatingModal({ orderId: 123, recentOrderCount: 10 });
 *
 * // Check what modal should be displayed
 * const currentModal = getCurrentModal();
 * if (currentModal?.type === 'rating') {
 *   return <RatingModal {...currentModal.args} />;
 * }
 * ```
 */
export const useModalStore = create<ModalStore>((set, get) => ({
  ...initialState,

  setRatingModal: (args: RatingModalArgs | null) => set({ ratingModal: args }),

  clearAllModals: () => set(initialState),

  getCurrentModal: () => {
    const state = get();

    // Priority order: rating modal first (add other modals here in priority order)
    if (state.ratingModal) {
      return { type: 'rating' as const, args: state.ratingModal };
    }

    return null;
  },

  resetStore: () => set(initialState),
}));
